import { Avatar, Card, Divider, Grid, Button, Chip } from "@mui/material";
import { generateAvatar } from "app/utils/avatar";

import { useState } from "react";
import PatientLiveCall from "components/pages/nonCompliantPatients/popover/PatientLiveCall";
import MarkTaskCompleteModal from "pages/liveCallTasks/MarkTaskCompleteModal";

import {
  LIVE_CALL_STATUS,
  MIN_ATTEMPTS_FOR_MANUAL_LIVE_CALL_TASK_COMPLETE,
} from "static/constants";

const LiveCallTaskItem = ({ patient, status, handleCallLogSubmit }) => {
  const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);

  const handlePatientProfile = (event, patientInfo) => {
    event.preventDefault();
    event.stopPropagation();

    const url = `/patient/${patientInfo?.patient_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const isManuallyCompleted =
    status === LIVE_CALL_STATUS.completed &&
    patient?.completion_type === "manual";

  const canManuallyComplete =
    status === LIVE_CALL_STATUS.need_follow_up &&
    patient?.call_attempt_count >=
      MIN_ATTEMPTS_FOR_MANUAL_LIVE_CALL_TASK_COMPLETE;

  return (
    <Card
      style={{
        padding: 12,
        margin: "8px 0",
        boxShadow: "none",
        ...(isManuallyCompleted && {
          backgroundColor: "#FFFBF3",
          border: "1px solid #E4D8B3",
          boxShadow: "-2px 0px 0px 0px #DEA651",
        }),
      }}
    >
      <Grid container>
        <Grid item xs={1}>
          <Avatar
            sx={{
              backgroundColor: isManuallyCompleted ? "#FFF4E5" : "#eaf2ff",
              color: isManuallyCompleted ? "#A0650E" : "#175ec8",
              height: "30px",
              width: "30px",
              fontWeight: 500,
            }}
            alt={patient?.name}
          >
            <div style={{ fontSize: "12px" }}>
              {patient?.name && generateAvatar(patient?.name)}
            </div>
          </Avatar>
        </Grid>

        <Grid item xs={9} style={{ paddingLeft: 8 }}>
          <p
            style={{
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
            onClick={(event) => handlePatientProfile(event, patient)}
          >
            {patient?.name}
          </p>
          <p style={{ fontSize: "14px", fontWeight: 400, color: "#949494" }}>
            EVOKE ID: {patient?.evoke_id}
          </p>
          <p style={{ fontSize: "14px", fontWeight: 400, color: "#949494" }}>
            Manufacturer: {patient?.manufacturer}
          </p>
          <p style={{ fontSize: "14px", fontWeight: 400, color: "#949494" }}>
            Account: {patient?.account}
          </p>

          {[LIVE_CALL_STATUS.need_follow_up].includes(status) && (
            <div
              className="mt-2"
              style={{
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
              }}
            >
              Follow-up status:
              <Chip
                label={`${patient?.call_attempt_count || 0} Attempt${
                  patient?.call_attempt_count === 1 ? "" : "s"
                }`}
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: "#EAF3DE",
                  color: "#588A3D",
                  fontWeight: 500,
                }}
              />
            </div>
          )}

          {patient?.call_summary && status !== "todo" && (
            <div className="mt-2" style={{ fontWeight: 400, fontSize: "14px" }}>
              Summary:
              <br />
              <span style={{ color: "#757575" }}>{patient?.call_summary}</span>
              <div style={{ color: "#949494", marginTop: 8 }}>
                {patient?.called_by ? patient?.called_by + " • " : ""}
                {patient?.call_duration ? patient?.call_duration + " • " : ""}
                {patient?.call_status
                  ? patient?.call_status === "failed"
                    ? "Not Received"
                    : "Received"
                  : ""}
              </div>
            </div>
          )}

          {status === LIVE_CALL_STATUS.completed &&
            isManuallyCompleted &&
            patient?.completion_reason && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <div
                  className="mt-2"
                  style={{ fontWeight: 400, fontSize: "14px" }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Follow-up notes:{" "}
                    <Chip
                      label={`${patient?.call_attempt_count || 0} Attempt${
                        patient?.call_attempt_count === 1 ? "" : "s"
                      }`}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor: "#F7ECCF",
                        color: "#7A5A1E",
                        fontWeight: 500,
                      }}
                    />
                  </div>
                  <span style={{ color: "#757575" }}>
                    {patient?.completion_reason}
                  </span>
                  <div style={{ color: "#949494", marginTop: 8 }}>
                    {patient?.completed_by ? patient?.completed_by + " • " : ""}
                    {patient?.call_status
                      ? patient?.call_status === "failed"
                        ? "Not Received"
                        : "Received"
                      : ""}
                  </div>
                </div>
              </>
            )}
        </Grid>

        {[LIVE_CALL_STATUS.to_do, LIVE_CALL_STATUS.need_follow_up].includes(
          status
        ) && (
          <Grid
            item
            xs={2}
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-end"
          >
            {canManuallyComplete ? (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  disableElevation
                  sx={{
                    height: "35px",
                    minWidth: "auto",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    backgroundColor: "#94BA33",
                    fontSize: "14px",
                    fontWeight: 400,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#86A82E" },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowMarkCompleteModal(true);
                  }}
                >
                  Complete
                </Button>
                {showMarkCompleteModal && (
                  <MarkTaskCompleteModal
                    taskId={patient?.task_id}
                    show={showMarkCompleteModal}
                    onCancel={() => setShowMarkCompleteModal(false)}
                    onSubmit={() => handleCallLogSubmit()}
                  />
                )}
              </>
            ) : (
              <PatientLiveCall
                isTask={true}
                patientId={patient?.patient_id}
                taskId={patient?.task_id}
                style={{ height: "36px", minWidth: "36px" }}
                onSubmit={() => {
                  handleCallLogSubmit();
                }}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default LiveCallTaskItem;
